/** @jsxImportSource @emotion/react */
import { UploadOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { Form, FormInstance, message, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { useEffect, useState } from 'react';
import CKEditorMaster from './CKEditor/CKEditorMaster';
import CustomLabel from './custom-label';
import { AdviceDetail } from '@/types/advice';
import { convertFileToBase64 } from '@/utils/common';

type ImageUploadWithEditorProps = {
  title: string;
  isRequired?: boolean;
  form: FormInstance<AdviceDetail>;
  isUploadImage?: boolean;
  item: AdviceDetail;
  onUpdateDetail?: (detail: AdviceDetail) => void;
  isAdjust: boolean;
};

export const ImageUploadWithEditor = ({
  title,
  isRequired = false,
  form,
  isUploadImage = true,
  item,
  onUpdateDetail,
  isAdjust
}: ImageUploadWithEditorProps) => {
  const getImageName = (url?: string) => {
    if (!url) return '';
    const fileName = url.substring(url.lastIndexOf('/') + 1);
    return fileName.split('?')[0];
  };
  const [fileList, setFileList] = useState<UploadFile[]>(
    item.IMAGE && typeof item.IMAGE === 'string'
      ? [
        {
          uid: '-1',
          name: getImageName(item?.IMAGE as string),
          status: 'done',
          url: item.IMAGE,
          thumbUrl: item.IMAGE,
          type: 'image/*'
        } as UploadFile
      ]
      : []
  );

  useEffect(() => {
    return () => {
      fileList.forEach((file) => {
        if (file.url && file.url.startsWith('blob:')) {
          URL.revokeObjectURL(file.url);
        }
      });
    };
  }, [fileList]);

  const handleChange = async ({ fileList }: { fileList: UploadFile[] }) => {
    const fileItem = fileList[0];
    const file = fileItem?.originFileObj as File | undefined;

    if (file) {
      const base64 = await convertFileToBase64(file);
      form.setFieldsValue({ IMAGE: base64 });

      const blobUrl = URL.createObjectURL(file);

      const updatedFileList = [
        {
          uid: fileItem.uid,
          name: file.name,
          status: 'done' as const,
          url: blobUrl,
          thumbUrl: blobUrl,
          type: file.type,
          size: file.size,
          originFileObj: file
        } as UploadFile
      ];

      setFileList(updatedFileList);

      onUpdateDetail?.({
        ...item,
        IMAGE: base64,
        FILENAME: file.name
      });
    } else {
      setFileList(fileList);
      form?.setFieldsValue({ IMAGE: '', FILENAME: '' });
      onUpdateDetail?.({
        ...item,
        IMAGE: '',
        FILENAME: ''
      });
    }
  };

  const handleRemove = () => {
    if (!isAdjust) return;

    fileList.forEach((file) => {
      if (file.url && file.url.startsWith('blob:')) {
        URL.revokeObjectURL(file.url);
      }
    });

    setFileList([]);
    form?.setFieldsValue({ IMAGE: undefined, FILENAME: '' });
    onUpdateDetail?.({
      ...item,
      IMAGE: '',
      FILENAME: ''
    });
  };

  const ALLOWED_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml'
  ];

  const beforeUpload = (file: File) => {
    const isValidType = ALLOWED_TYPES.includes(file.type);
    const fileExtension = file.name
      .slice(file.name.lastIndexOf('.'))
      .toLowerCase();
    const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];

    if (!isValidType || !validExtensions.includes(fileExtension)) {
      message.error('Chỉ chấp nhận file hình ảnh');
      return Upload.LIST_IGNORE;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Kích thước ảnh phải nhỏ hơn 5MB.');
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const handleContentChange = (newContent: string) => {
    form?.setFieldsValue({ CONTENT: newContent });

    onUpdateDetail?.({
      ...item,
      CONTENT: newContent
    });
  };

  return (
    <div css={wrapperStyle}>
      <div className='label'>
        <CustomLabel text={title} isRequired={isRequired} />
      </div>

      <div className='content'>
        {isUploadImage && (
          <div className='upload-container'>
            <Form.Item
              name='IMAGE'
            // rules={
            //   isRequired
            //     ? [{ required: true, message: 'Vui lòng chọn hình ảnh' }]
            //     : []
            // }
            >
              <Upload
                disabled={!isAdjust}
                listType='picture'
                fileList={fileList}
                onChange={handleChange}
                onRemove={handleRemove}
                beforeUpload={beforeUpload}
                maxCount={1}
                accept='.png, .jpeg, .jpg'
                showUploadList={{ showRemoveIcon: isAdjust }}
              >
                <button type='button' className='upload-button'>
                  <UploadOutlined /> Chọn để tải lên hình ảnh
                </button>
              </Upload>
            </Form.Item>

            <Form.Item name='FILENAME' hidden>
              <input type='hidden' />
            </Form.Item>
          </div>
        )}

        <Form.Item
          name='CONTENT'
          rules={
            isRequired
              ? [{ required: true, message: 'Vui lòng nhập mô tả' }]
              : []
          }
        >
          <div className='editor'>
            <CKEditorMaster
              MAX_CHAR_LENGTH={5000}
              isDisabled={!isAdjust}
              content={item?.CONTENT || null}
              handleUpdateContent={handleContentChange}
              placeholder='Nhập mô tả'
            />
          </div>
        </Form.Item>
      </div>
    </div>
  );
};

const wrapperStyle = css`
  display: grid;
  grid-template-columns: 2fr 8fr;
  align-items: flex-start;
  gap: 12px;

  .label {
    padding-top: 8px;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .upload-container {
    display: flex;
    align-items: flex-start;
    gap: 12px;

    .ant-form-item {
      margin-bottom: 0;
    }

    .ant-upload-wrapper {
      display: flex;
      align-items: flex-start;
      gap: 12px;

      .ant-upload-list {
        margin: 0;

        .ant-upload-list-item {
          height: 36px;
          .ant-upload-list-item-thumbnail {
            height: 26px;
            width: 26px;
          }
          margin-bottom: 0 !important;
          margin-top: 0 !important;

          .ant-upload-list-item-name {
            max-width: 150px;
          }
        }
      }

      .ant-upload-select {
        margin: 0;
      }
    }
  }

  .upload-button {
    border: 1px solid #d9d9d9;
    padding: 6px 12px;
    border-radius: 4px;
    background: #fafafa;
    cursor: pointer;
  }

  .editor {
    margin-top: 8px;
    ul li,
    ol li,
    nav ul {
      list-style: revert !important;
    }

    .ck.ck-content.ck-editor__editable.ck-rounded-corners.ck-editor__editable_inline {
      padding: 0.5rem 3rem;
    }
  }
`;
